import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, MapPinned } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClientPhone, RescheduleTaskButton } from "@/components/TaskExtras";
import { VisitHistory } from "@/components/RescheduleDialog";

/** Create a manual task (pass `defaultDue`) or edit an existing one (pass `task`). */
export default function TaskDialog({ open, onOpenChange, task, defaultDue, onReschedule }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({ title: "", due_at: "", notes: "", done: false, owner_id: "" });

  useEffect(() => {
    if (!open) return;
    setF({
      title: task?.title || "",
      due_at: task?.due_at || defaultDue || "",
      notes: task?.notes || "",
      done: Boolean(task?.done),
      owner_id: task?.owner_id || user?.id || "",
    });
  }, [open, task, defaultDue, user?.id]);

  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: open && isAdmin && !task,
    staleTime: 5 * 60_000,
  });

  const refresh = () => ["tasks", "opportunities", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const saveMut = useMutation({
    mutationFn: async () => {
      if (task) return api.patch(`/tasks/${task.id}`, { title: f.title, due_at: f.due_at, notes: f.notes, done: f.done });
      return api.post("/tasks", { title: f.title, due_at: f.due_at, notes: f.notes, owner_id: isAdmin ? f.owner_id : undefined });
    },
    onSuccess: () => { toast.success(task ? "Task updated" : "Task added"); refresh(); onOpenChange(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save task"),
  });
  const delMut = useMutation({
    mutationFn: async () => api.delete(`/tasks/${task.id}`),
    onSuccess: () => { toast.success("Task deleted"); refresh(); onOpenChange(false); },
    onError: () => toast.error("Delete failed"),
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const isVisit = task?.kind === "visit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="text-navy">{task ? (isVisit ? "Site visit" : "Edit task") : "New task"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {task && (task.lead_phone || !task.done) && (
            <div className="flex flex-wrap items-center gap-2" data-testid="task-dialog-quick-actions">
              <ClientPhone task={task} />
              {onReschedule && <RescheduleTaskButton task={task} onClick={onReschedule} testid="task-dialog-reschedule-btn" />}
            </div>
          )}
          {task && <VisitHistory history={task.due_history} testid="task-dialog-history" label="reschedule history" />}
          <div>
            <Label className="mb-1 block text-xs text-slate-500">Title</Label>
            <Input data-testid="task-title-input" value={f.title} onChange={set("title")} placeholder="e.g. Call back Mr. Shah" />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-slate-500">Due date & time</Label>
            <Input data-testid="task-due-input" type="datetime-local" value={f.due_at} onChange={set("due_at")} />
          </div>
          {isAdmin && !task && (
            <div>
              <Label className="mb-1 block text-xs text-slate-500">Assign to</Label>
              <Select value={f.owner_id} onValueChange={set("owner_id")}>
                <SelectTrigger data-testid="task-owner-select"><SelectValue placeholder="Me" /></SelectTrigger>
                <SelectContent>
                  {(usersQ.data || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="mb-1 block text-xs text-slate-500">Notes</Label>
            <Textarea data-testid="task-notes-input" rows={3} value={f.notes} onChange={set("notes")} />
          </div>
          {task && (
            <label className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
              <span className="text-sm font-medium text-slate-700">{isVisit ? "Visit Done" : "Completed"}</span>
              <Switch data-testid="task-done-switch" checked={f.done} onCheckedChange={set("done")} />
            </label>
          )}
          {isVisit && (
            <Link to="/opportunities" data-testid="task-open-opp" className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline">
              <MapPinned className="h-3.5 w-3.5" /> Linked to an opportunity
            </Link>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {task ? (
            <Button
              variant="ghost"
              data-testid="task-delete-btn"
              className="text-rose-600 hover:bg-rose-50"
              onClick={() => window.confirm("Delete this task?") && delMut.mutate()}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          ) : <span />}
          <Button
            data-testid="task-save-btn"
            className="bg-navy hover:brightness-110"
            disabled={!f.title.trim() || !f.due_at || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : task ? "Save" : "Add task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
